import { FC, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { api, Id } from "@/smart-views/client/api";
import { ChippyText } from "./chips";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
} from "@/mds";
import { SourceRunCitations } from "../citations/SourceRunCitations";
import { getVisibleSourceIdsForRepeatedRun } from "../citations/source-utils";

interface Pair {
  key: string;
  value: string;
  sources?: string[];
}

interface KeyValueBlockProps {
  blockId: Id<"blocks">;
  pairs: Pair[];
  isReadOnly?: boolean;
  onUserAction?: (summary: string) => void;
}

export const KeyValueBlock: FC<KeyValueBlockProps> = ({
  blockId,
  pairs,
  isReadOnly = false,
  onUserAction,
}) => {
  const updateContent = useMutation(api.smartViews.client.updateBlockContent);
  const [draft, setDraft] = useState<Pair[]>(pairs);
  const lastCommittedRef = useRef<Pair[]>(pairs);
  // Click-to-edit values that contain chips so the chip rendering is visible.
  // Mount-time autoFocus on the input handles "focus when entering edit mode".
  const [editingValueIdx, setEditingValueIdx] = useState<number | null>(null);

  useEffect(() => {
    setDraft(pairs);
    lastCommittedRef.current = pairs;
  }, [pairs]);

  const commit = (next: Pair[]) => {
    updateContent({ blockId, content: { kind: "keyValue", pairs: next } });
  };

  const updatePair = (idx: number, patch: Partial<Pair>) => {
    setDraft(curr => curr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const commitWithNotify = (next: Pair[]) => {
    const prev = lastCommittedRef.current;
    commit(next);
    lastCommittedRef.current = next;
    if (!onUserAction) return;
    const changedIdx = next.findIndex((p, i) => prev[i] !== undefined && p.value !== prev[i].value);
    if (changedIdx === -1) return;
    const pair = next[changedIdx];
    const label = pair.key.trim() || "(unlabeled)";
    onUserAction(`Changed "${label}" to "${pair.value}".`);
  };

  const addPair = () => {
    const next = [...draft, { key: "", value: "" }];
    setDraft(next);
    commit(next);
    lastCommittedRef.current = next;
  };

  const removePair = (idx: number) => {
    const removed = draft[idx];
    const next = draft.filter((_, i) => i !== idx);
    setDraft(next);
    commit(next);
    lastCommittedRef.current = next;
    if (removed && onUserAction) {
      const label = removed.key.trim() || "(unlabeled)";
      onUserAction(`Removed the field "${label}".`);
    }
  };

  return (
    <Grid>
      {draft.map((pair, idx) => {
        const visibleSourceIds = getVisibleSourceIdsForRepeatedRun({
          sourceIds: pair.sources,
          previousSourceIds: draft[idx - 1]?.sources,
        });
        return (
          <Row key={idx}>
            <CutContentTooltip label={pair.key}>
              {({ inputRef, updateClippedState }) => (
                <FieldTooltipTrigger
                  className="key-value-key"
                  onMouseEnter={updateClippedState}
                  onFocus={updateClippedState}
                >
                  <KeyInput
                    ref={inputRef}
                    value={pair.key}
                    placeholder="LABEL"
                    readOnly={isReadOnly}
                    onChange={e => updatePair(idx, { key: e.target.value })}
                    onBlur={() => commit(draft)}
                  />
                </FieldTooltipTrigger>
              )}
            </CutContentTooltip>
            <ValueStack className="key-value-value">
              {(() => {
                const isEditingValue = editingValueIdx === idx;

                if (!isReadOnly && isEditingValue) {
                  return (
                    <CutContentTooltip label={pair.value}>
                      {({ inputRef, updateClippedState }) => (
                        <FieldTooltipTrigger
                          onMouseEnter={updateClippedState}
                          onFocus={updateClippedState}
                        >
                          <ValueInput
                            ref={inputRef}
                            autoFocus
                            value={pair.value}
                            placeholder="Value"
                            readOnly={isReadOnly}
                            onChange={e => updatePair(idx, { value: e.target.value })}
                            onBlur={() => {
                              commitWithNotify(draft);
                              setEditingValueIdx(null);
                            }}
                          />
                        </FieldTooltipTrigger>
                      )}
                    </CutContentTooltip>
                  );
                }

                return (
                  <ValueViewer
                    $clickable={!isReadOnly}
                    onClick={isReadOnly ? undefined : () => setEditingValueIdx(idx)}
                  >
                    <ValueText>
                      {pair.value.length > 0 ? (
                        <ChippyText text={pair.value} inline />
                      ) : (
                        <ValuePlaceholder>Value</ValuePlaceholder>
                      )}
                    </ValueText>
                  </ValueViewer>
                );
              })()}
              <SourceRunCitations visibleSourceIds={visibleSourceIds} maxWidth="min(260px, 100%)" />
            </ValueStack>
            {!isReadOnly && (
              <RemoveSlot className="key-value-remove">
                <MDSIconButton
                  size={MDSIconButtonSize.XSmall}
                  iconKind={MDSIconKind.Trash}
                  variant={MDSIconButtonVariant.OnChrome}
                  onClick={() => removePair(idx)}
                  rounded
                />
              </RemoveSlot>
            )}
          </Row>
        );
      })}
      {!isReadOnly && <AddRow onClick={addPair}>+ Add field</AddRow>}
    </Grid>
  );
};

const CutContentTooltip: FC<{
  label: string;
  children: (props: {
    inputRef: RefObject<HTMLInputElement>;
    updateClippedState: () => void;
  }) => ReactNode;
}> = ({ label, children }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isClipped, setIsClipped] = useState(false);
  const updateClippedState = useCallback(() => {
    const input = inputRef.current;

    if (!input) {
      setIsClipped(false);
      return;
    }

    setIsClipped(input.scrollWidth > input.clientWidth);
  }, []);

  useEffect(() => {
    updateClippedState();
  }, [label, updateClippedState]);

  return (
    <MDSTooltip
      config={
        label && isClipped
          ? {
              label,
              placement: MDSTooltipPlacement.Top,
            }
          : undefined
      }
    >
      {children({ inputRef, updateClippedState })}
    </MDSTooltip>
  );
};

const Grid = styled.div`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const KeyInput = styled.input`
  border: none;
  background: transparent;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
  outline: none;
  padding: 4px 0;
  font-family: inherit;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const FieldTooltipTrigger = styled(MDSTooltipTrigger)`
  display: flex;
  flex: 1 1 140px;
  min-width: 0;
`;

const ValueInput = styled.input`
  border: none;
  background: transparent;
  font-size: 14px;
  color: inherit;
  outline: none;
  padding: 4px 0;
  font-family: inherit;
  border-radius: 4px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  &:focus {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
    padding: 4px 6px;
    margin: 0 -6px;
  }
`;

const ValueViewer = styled.div<{ $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  flex: 0 1 auto;
  font-size: 14px;
  line-height: 1.4;
  padding: 4px 0;
  min-width: 0;
  max-width: 100%;
  cursor: ${({ $clickable }) => ($clickable ? "text" : "default")};
`;

const ValueText = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const ValuePlaceholder = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const ValueStack = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 2px;
  min-width: 0;
`;

const RemoveSlot = styled.div`
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: min(200px, 38%) minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing["2"]};
  align-items: center;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing["2"]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-of-type {
    padding-top: 0;
  }

  @container (max-width: 400px) {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;

    .key-value-key {
      grid-column: 1;
      grid-row: 1;
      min-width: 0;
    }

    .key-value-value {
      grid-column: 1;
      grid-row: 2;
      min-width: 0;
    }

    .key-value-remove {
      grid-column: 2;
      grid-row: 1 / span 2;
    }
  }

  &:hover > div {
    opacity: 1;
  }
`;

const AddRow = styled.button`
  background: transparent;
  border: none;
  text-align: left;
  padding: 4px 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 13px;
  font-family: inherit;
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;
